import CCText from '@/Atoms/CCText';
import CCTextInput from '@/Atoms/CCTextInput/CCTextInput';
import CCColors from '@/Utils/CCColors';
import {
  formatDate,
  formatDateToDDMMYYYY,
  formatTimeHHMM,
} from '@/Utils/CommonUtils';
import {FontSizes} from '@/Utils/Dimensions';
import {Ionicons} from '@expo/vector-icons';
import {FlatList, TouchableOpacity, View, Image} from 'react-native';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import {styles} from '../style';
import searchIcon from '../../../Assets/Images/png/search.png';
import NoIssueSelected from '../../../Assets/Images/Svgs/no_issue_selected.svg';
import {useEffect, useMemo, useState} from 'react';
import { useRoute, useNavigation } from '@react-navigation/native'

interface ChatListProps {
  search?: string;
  setSearch?: (text: string) => void;
  visible?: boolean;
  setVisible?: React.Dispatch<React.SetStateAction<boolean>>;
  sortBy: 'date' | 'status' | string;
  handleSelect: (sortType: 'date' | 'status') => void;
  ticketDetails: {
    id?: number | string;
    title?: string;
    comments?: any[];
    last_updated?: string;
    status: number;
    user_unread_count?: number;
  };
  filteredTickets: any[];
  selectedTicket: null;
  setSelectedTicket: (ticket) => void;
  setSelectedTicketId: (id: number | string) => void;
  showSearchBar: boolean;
  isTicketList?: boolean;
  showFilter?: boolean;
}

const ChatList: React.FC<ChatListProps> = ({
  search,
  setSearch,
  visible,
  setVisible,
  sortBy,
  handleSelect,
  filteredTickets,
  selectedTicket,
  setSelectedTicket,
  setSelectedTicketId,
  showSearchBar = false,
  isTicketList = false,
  showFilter = true,
}) => {
  const route = useRoute()
  const navigation = useNavigation()
  return (
    <View style={styles.leftPanel}>
      {showSearchBar && (
        <View style={styles.searchRow}>
          {/* Search Bar */}

          <CCTextInput
            key="support_tickets"
            icon={searchIcon}
            fontSize={FontSizes[14]}
            value={search}
            style={{
              flex: 1,
              height: 40,
              backgroundColor: CCColors.White,
              overflow: 'hidden',
            }}
            placeholder={isTicketList ? 'Search a Ticket' : 'Search group'}
            onChangeText={setSearch}
            placeholderTextColor={CCColors.TextColor.Grey}
          />

          {/* Filter Dropdown */}
          {showFilter && (
            <View>
              <TouchableOpacity
                style={styles.filterButton}
                onPress={() => setVisible(prev => !prev)}>
                <View style={{flexDirection: 'row', alignItems: 'flex-end'}}>
                  <MaterialCommunityIcons
                    name="arrow-up-thin"
                    size={26}
                    color={CCColors?.Primary?.Brown}
                    style={{marginRight: -6}}
                  />
                  <MaterialCommunityIcons
                    name="arrow-up-thin"
                    size={20}
                    color={CCColors?.Primary?.Brown}
                    style={{marginLeft: -6}}
                  />
                </View>
              </TouchableOpacity>

              {visible && (
                <View style={styles.dropdown}>
                  <TouchableOpacity
                    style={styles.dropdownItem}
                    onPress={() => handleSelect('date')}>
                    <MaterialCommunityIcons
                      name="calendar"
                      size={18}
                      color={CCColors?.Primary?.Brown}
                    />
                    <CCText style={styles.dropdownText}>By Date</CCText>
                    {sortBy === 'date' && (
                      <Ionicons
                        name="checkmark"
                        size={18}
                        color={CCColors?.Primary?.Brown}
                        style={styles.trailingIcon}
                      />
                    )}
                  </TouchableOpacity>

                  <View style={styles.separator} />

                  <TouchableOpacity
                    style={styles.dropdownItem}
                    onPress={() => handleSelect('status')}>
                    <View style={styles.iconCircleMobile}>
                      <Ionicons
                        name="checkmark"
                        size={11}
                        color={CCColors?.Primary?.Brown}
                      />
                    </View>
                    <CCText style={styles.dropdownText}>By Status</CCText>
                    {sortBy === 'status' && (
                      <Ionicons
                        name="checkmark"
                        size={18}
                        color={CCColors?.Primary?.Brown}
                        style={styles.trailingIcon}
                      />
                    )}
                  </TouchableOpacity>
                </View>
              )}
            </View>
          )}
        </View>
      )}

      {/* Ticket List / Empty State */}
      {filteredTickets?.length === 0 ? (
        <View style={styles.emptyState}>
          <Image
            source={NoIssueSelected}
            style={{width: 150, height: 150, marginBottom: 20}}
            resizeMode="contain"
          />
          <CCText
            fontSize={FontSizes[16]}
            color={CCColors?.TextColor?.Dark}
            style={styles.emptyText}>
            {isTicketList ? 'No tickets found' : 'No conversations found'}
          </CCText>
          {isTicketList && (
            <CCText
              fontSize={FontSizes[14]}
              color={CCColors?.TextColor?.Dark}
              style={styles.emptySubText}>
              Try searching with another keyword
            </CCText>
          )}
        </View>
      ) : (
        <FlatList
          data={filteredTickets}
          keyExtractor={item => item.id.toString()}
          renderItem={({item}) => (
            <TouchableOpacity
              style={[
                styles.ticketItem,
                selectedTicket?.id === item.id && styles.ticketSelected,
              ]}
              onPress={() => {
                setSelectedTicket(item);
                setSelectedTicketId(item.id);
                if (route.params) {
                  navigation.setParams({ ticketId: undefined, channelId: undefined });
                }
              }}>
              <View style={[styles.supportTicket, {alignItems: 'center'}]}>
                <CCText
                  fontSize={FontSizes[14]}
                  numberOfLines={1}
                  style={styles.ticketTitle}
                  color={CCColors?.TextColor?.Dark}>
                  {isTicketList ? item.title : item.name}
                </CCText>
                {isTicketList ? (
                  <View
                    style={[
                      styles.statusBadge,
                      styles[item.status],
                      selectedTicket?.id === item.id && {
                        backgroundColor: CCColors.White,
                      },
                    ]}>
                    <CCText fontSize={FontSizes[10]} style={styles.statusText}>
                      {item.status}
                    </CCText>
                  </View>
                ) : (
                  <View style={[styles.supportTicket]}>
                    {item.user_unread_count > 0 && (
                      <View style={[styles.unreadBadge, {marginBottom: 4}]}>
                        <CCText
                          fontSize={FontSizes[10]}
                          color={CCColors?.White}>
                          {item.user_unread_count}
                        </CCText>
                      </View>
                    )}
                  </View>
                )}
              </View>

              <View style={styles.ticketMeta}>
                {isTicketList ? (
                  <View style={[styles.supportTicket, {gap: 8}]}>
                    <CCText
                      fontSize={FontSizes[12]}
                      color={
                        selectedTicket?.id === item.id
                          ? CCColors?.TextColor?.LightGrey
                          : CCColors?.TextColor?.Grey
                      }>
                      # {item.id}
                    </CCText>
                    <CCText
                      fontSize={FontSizes[12]}
                      color={
                        selectedTicket?.id === item.id
                          ? CCColors?.TextColor?.LightGrey
                          : CCColors?.TextColor?.Grey
                      }>
                      <MaterialCommunityIcons
                        name="calendar"
                        size={FontSizes[10]}
                        color={
                          selectedTicket?.id === item.id
                            ? CCColors?.TextColor?.LightGrey
                            : CCColors?.TextColor?.Grey
                        }
                      />
                      {item.last_updated
                        ? formatDateToDDMMYYYY(item.last_updated)
                        : '—'}
                    </CCText>
                  </View>
                ) : (
                  <View style={[styles.supportTicket]}>
                    <CCText
                      fontSize={FontSizes[12]}
                      color={
                        selectedTicket?.id === item.id
                          ? CCColors?.TextColor?.LightGrey
                          : CCColors?.TextColor?.Grey
                      }>
                      {item.body != null
                        ? item.body.length > 30
                          ? `${item.body.slice(0, 25)}...`
                          : item.body
                        : '—'}
                    </CCText>
                  </View>
                )}

                <View style={[styles.supportTicket, {gap: 6}]}>
                  {isTicketList && item.user_unread_count > 0 && (
                    <View style={styles.unreadBadge}>
                      <CCText fontSize={FontSizes[10]} color={CCColors?.White}>
                        {item.user_unread_count}
                      </CCText>
                    </View>
                  )}
                  <CCText
                    fontSize={FontSizes[10]}
                    color={
                      selectedTicket?.id === item.id
                        ? CCColors?.TextColor?.LightGrey
                        : CCColors?.TextColor.GreyText
                    }>
                    {item.last_updated
                      ? isTicketList
                        ? formatDate(item.last_updated).split('@')[1]
                        : formatDate(item.last_updated).split('@')[0] +
                          formatDate(item.last_updated).split('@')[1]
                      : '—'}
                  </CCText>
                </View>
              </View>
            </TouchableOpacity>
          )}
          style={{maxHeight: 7 * 95}}
          showsVerticalScrollIndicator={false}
        />
      )}
    </View>
  );
};

export default ChatList;
